/**
 * Centralized Color Constants
 *
 * Single source of truth for all semantic colors used in JS/TS.
 * CSS files should use the corresponding CSS custom properties from index.css.
 *
 * Categories:
 *   - CAMEL component colors (6 risk dimensions)
 *   - Report chart brand colors (Pantone-based)
 *   - Performance tier colors
 *   - Rating scale colors
 *   - Score-based colors for SVG elements
 */

import type { CAMELCode } from './camelConstants';

// =============================================================================
// CAMEL COMPONENT COLORS
// =============================================================================

/**
 * Color for each CAMEL risk dimension.
 * Used in CAMELFlowchart, CAMELSankeyFlow, radar charts, and gauge displays.
 */
export const CAMEL_COLORS: Record<string, string> = {
  capital:      '#6366f1',  // Indigo — Capital / Solvency (C)
  efficiency:   '#8b5cf6',  // Purple — Efficiency & Management (EG)
  liquidity:    '#06b6d4',  // Cyan — Liquidity (L)
  profitability:'#10b981',  // Green — Profitability (R)
  assetQuality: '#f59e0b',  // Amber — Asset Quality (S)
  sensitivity:  '#ef4444',  // Red — Interest Rate Sensitivity (STI)
};

/**
 * CAMEL colors indexed by CAMEL code (C, EG, L, R, S, STI).
 */
export const CAMEL_COLORS_BY_CODE: Record<CAMELCode, string> = {
  C:   CAMEL_COLORS.assetQuality,
  EG:  CAMEL_COLORS.efficiency,
  L:   CAMEL_COLORS.liquidity,
  R:   CAMEL_COLORS.profitability,
  S:   CAMEL_COLORS.capital,
  STI: CAMEL_COLORS.sensitivity,
};

/**
 * Map from Spanish category name → dimension metadata.
 * Used by CAMELSankeyFlow to resolve indicator categories to colors.
 */
export const CATEGORY_DIMENSION_MAP: Record<string, { code: string; name: string; color: string }> = {
  'Solvencia':                   { code: 'CAP', name: 'Capital',                    color: CAMEL_COLORS.capital },
  'Eficiencia y Gestion':        { code: 'EFF', name: 'Efficiency & Earnings',      color: CAMEL_COLORS.efficiency },
  'Liquidez':                    { code: 'LIQ', name: 'Liquidity',                  color: CAMEL_COLORS.liquidity },
  'Rentabilidad':                { code: 'PRO', name: 'Profitability',              color: CAMEL_COLORS.profitability },
  'Calidad de Activos':          { code: 'ASQ', name: 'Asset Quality',              color: CAMEL_COLORS.assetQuality },
  'Sensibilidad Tasa Interes':   { code: 'SEN', name: 'Interest Rate Sensitivity',  color: CAMEL_COLORS.sensitivity },
};

// =============================================================================
// REPORT CHART COLORS (Pantone-based brand palette)
// =============================================================================

/**
 * Standard chart palette for report Graficos (04–13).
 * Based on Pantone brand colors for printed reports.
 */
export const REPORT_CHART_COLORS = {
  primary:   '#343E48',  // Dark gray (Pantone 432 C)
  secondary: '#3AB0C8',  // Teal (Pantone 631 C)
  accent:    '#f44336',  // Red — for risk/warning indicators
  tertiary:  '#9e9e9e',  // Medium gray — third series
};

// =============================================================================
// PERFORMANCE COLORS
// =============================================================================

/**
 * Tier colors for performance assessment (used in gauges, badges, etc.).
 */
export const PERFORMANCE_COLORS = {
  EXCELLENT: '#059669',  // Dark Green — AAA/AA
  STRONG:    '#10b981',  // Green — A or better
  ADEQUATE:  '#fbbf24',  // Yellow — BBB/BB
  WEAK:      '#ef4444',  // Red — B or worse
  CRITICAL:  '#dc2626',  // Dark Red — C/D/E
};

// =============================================================================
// RATING SCALE COLORS
// =============================================================================

/**
 * Hex colors for each rating grade.
 * Used in RATING_SCALE definitions and rating badges.
 */
export const RATING_COLORS: Record<string, string> = {
  AAA: '#10b981',  // green-500
  AA:  '#22c55e',  // green-400
  A:   '#84cc16',  // lime-500
  BBB: '#eab308',  // yellow-500
  BB:  '#f97316',  // orange-500
  B:   '#f59e0b',  // amber-500
  CCC: '#ef4444',  // red-500
  CC:  '#dc2626',  // red-600
  C:   '#b91c1c',  // red-700
  E:   '#9ca3af',  // gray-400 (insufficient data)
};

// =============================================================================
// SCORE-BASED COLORS (for SVG elements that can't use CSS vars)
// =============================================================================

/**
 * Get hex color based on rating index position.
 * For SVG elements (gauges, charts) that need raw hex values.
 */
export const SCORE_HEX_COLORS = {
  excellent: '#00D4AA',  // Teal — top tier
  good:      '#4ADE80',  // Light green — good
  adequate:  '#F5A623',  // Orange — adequate
  moderate:  '#FF8C42',  // Dark orange — moderate
  weak:      '#FF4757',  // Red — weak
};

// =============================================================================
// MAP / LEGEND COLORS
// =============================================================================

/**
 * Rating colors used in the RatingsMap choropleth and legend.
 */
export const MAP_RATING_COLORS: Record<string, string> = {
  AAA:  '#0d4715',  // Dark green
  AA:   '#1b5e20',  // Forest green
  A:    '#4caf50',  // Green
  BBB:  '#ffc107',  // Amber
  BB:   '#ff9800',  // Orange
  B:    '#f44336',  // Red
  'N/A': '#e0e0e0', // Light gray
};

// =============================================================================
// STATUS COLORS (for report workflow badges)
// =============================================================================

/**
 * Status badge colors for BenchHome report workflow.
 */
export const STATUS_COLORS: Record<string, { color: string; bg: string }> = {
  draft:             { color: '#6b7280', bg: 'rgba(107, 114, 128, 0.1)' },
  pending_admin:     { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)' },
  client_review:     { color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)' },
  pending_committee: { color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.1)' },
  rejected:          { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)' },
};
